import { ShapeAttrs } from '@antv/g-base';
import isPlainObject from 'lodash/isPlainObject';
import merge from 'lodash/merge';

import { colors } from 'ui/theme';
import { ImageSegment, Segment, Shape } from 'ui/workflows/flowchart/types';
import { shapeNames } from '../../constants';
import { addLinkButtonToText, addLinkToText } from '../linkUtils';
import { drawTextBackground, drawTextUnderline, getTextWidth } from '../textUtils';
import { addTooltipToText } from '../tooltipUtils';
import { DrawSegmentArgs, DrawSegmentReturn } from './types';

const isImageSegment = (segment: Segment): segment is ImageSegment =>
  typeof (segment as ImageSegment)?.img === 'string';

const urlStyle = { fill: colors.textLink, cursor: 'pointer' };

const isFiniteNumber = (value?: number): value is number => Number.isFinite(value);

/** Gets the height of the line from the style */
const getStyleHeight = ({ lineHeight, fontSize }: DrawSegmentArgs['style']) =>
  lineHeight ?? fontSize ?? 0;

/** Gets the vertical midpoint of a line of text */
const getLineMidpoint = (y: number, { lineHeight, fontSize }: DrawSegmentArgs['style'] = {}) => {
  const height = getStyleHeight({ lineHeight, fontSize });
  const fontLineHeightDiff =
    isFiniteNumber(lineHeight) && isFiniteNumber(fontSize) ? lineHeight - fontSize : 0;
  const heightMidpoint = height === 0 ? 0 : height / 2;

  return y - heightMidpoint + fontLineHeightDiff;
};

/** Retrieves the y position for an image */
const getImageY = (args: {
  y: number;
  segment: ImageSegment;
  parentStyle: DrawSegmentArgs['style'];
}) => {
  const { y, segment, parentStyle } = args;
  const { height: imageHeight } = segment;
  const heightDiff = getStyleHeight(parentStyle) - imageHeight;
  const midpoint = getLineMidpoint(y, parentStyle);

  if (heightDiff === 0) return midpoint;
  if (heightDiff < 0) return midpoint - heightDiff / 2;
  return midpoint + heightDiff / 2;
};

const getPropertiesForShape = (
  attrs: ShapeAttrs,
  { segment, rowNumber = 0, name }: DrawSegmentArgs,
) => {
  const { props = {}, isButtonLink = false } = segment;
  // If the text is a button link, we need to standardize the name so that the
  // onclick listener functions properly
  const segmentName = isButtonLink ? shapeNames.segmentButtonLink : name;

  return merge(
    { name: segmentName, className: segmentName, draggable: true, rowNumber },
    { attrs },
    props,
  );
};

/**
 * Draws an individual segment
 */
export const drawSegment = (opts: DrawSegmentArgs): DrawSegmentReturn => {
  const { group, maxWidth = Infinity, segment, style: parentStyle, x, y } = opts;
  const {
    background,
    isButtonLink = false,
    isUnderlined = false,
    style: segmentStyle = {},
    tooltipText,
    url,
  } = segment;
  let shape: Shape | null = null;
  let shapeWidth;
  let shapeHeight = 0;

  if (isImageSegment(segment)) {
    const { img, width, height } = segment;
    // TODO: [WF-1350] figure out what to do if the image should be cut off due
    // to max width
    shape = group.addShape(
      'image',
      getPropertiesForShape(
        {
          x,
          // vertically center image with text
          y: getImageY({ y, segment, parentStyle }),
          img,
          width,
          height,
          ...segmentStyle,
        },
        opts,
      ),
    );
    shapeWidth = width;
    shapeHeight = height;
  } else {
    const { text = '' } = segment;
    const attrs = { ...parentStyle, y, text, ...(url ? urlStyle : {}), ...segmentStyle, x };
    shape = group.addShape('text', getPropertiesForShape(attrs, opts));
    shapeWidth = getTextWidth(text, parentStyle);
    shapeHeight = attrs.lineHeight || attrs.fontSize || 0;
  }

  if (url) {
    addLinkToText({ shape, group, url, maxWidth, isUnderlined: false });
  } else if (isButtonLink) {
    addLinkButtonToText({ shape, group, maxWidth, isUnderlined: false });
  }

  if (tooltipText) {
    addTooltipToText({ shape, group, tooltipText, maxWidth, isUnderlined: false });
  }

  // Properties that are underlined by default
  const isUnderlinedByDefault = !!(url || isButtonLink || tooltipText);

  /**
   * Add an underline to the segment if:
   *  1. `isUnderlined` is true
   *  2. `url` or `tooltipText` is defined, with an ability to override this if
   *      `segment.isUnderlined` is specifically set to false
   */
  if (isUnderlined || (isUnderlinedByDefault && segment.isUnderlined !== false)) {
    const isDashed =
      typeof segment.isDashed === 'boolean' ? segment.isDashed : !!isUnderlinedByDefault;

    drawTextUnderline({
      shape,
      group,
      isDashed,
      maxWidth,
      style: { lineWidth: 1, ...parentStyle, ...segmentStyle, y },
    });
  }

  if (isPlainObject(background)) {
    drawTextBackground({ shape, group, style: background });
  }

  return { shape, height: Math.max(shapeHeight, shape?.getBBox()?.height || 0), width: shapeWidth };
};
